import { inject } from '@angular/core';
import { DashboardAccess } from '../dashboard-access';
import { DASHBOARD_ACCESS, DASHBOARD_CONFIG } from '../dashboard-grid.tokens';
import { DashboardWidgetInstance } from '../layout/dashboard-layout';
import { DashboardWidgetDefinition, DashboardWidgetSize, maxInstancesOf } from './dashboard-widget-definition';

export interface DashboardCatalogEntry {
  definition: DashboardWidgetDefinition;
  count: number;
  multiple: boolean;
  /** The layout already has `maxInstances` of this type */
  atLimit: boolean;
}

/** The widget types registered for the current dashboard, judged for the current user. */
export class DashboardWidgetRegistry {
  private readonly byType: ReadonlyMap<string, DashboardWidgetDefinition>;

  constructor(
    private readonly definitions: readonly DashboardWidgetDefinition[],
    private readonly access: Pick<DashboardAccess, 'hasPermissions'>,
  ) {
    this.byType = new Map(definitions.map((definition) => [definition.type, definition]));
  }

  /** undefined for types this dashboard doesn't register */
  find(type: string): DashboardWidgetDefinition | undefined {
    return this.byType.get(type);
  }

  /** Unknown types are visible (they render the unavailable frame); known types need all their permissions */
  isVisible(widget: Pick<DashboardWidgetInstance, 'type'>): boolean {
    const definition = this.find(widget.type);
    return !definition || this.isPermitted(definition);
  }

  /** undefined for unknown types */
  sizeLimits(type: string): DashboardWidgetSize | undefined {
    return this.find(type)?.size;
  }

  /** Permitted, registered and below its `maxInstances` (default 1) in `widgets` */
  canAdd(type: string, widgets: readonly DashboardWidgetInstance[]): boolean {
    const definition = this.find(type);
    return !!definition && this.isPermitted(definition) && countOf(definition, widgets) < maxInstancesOf(definition);
  }

  /** Permitted definitions in registration order, for the Customize panel's widget list */
  catalog(widgets: readonly DashboardWidgetInstance[]): DashboardCatalogEntry[] {
    return this.definitions
      .filter((definition) => this.isPermitted(definition))
      .map((definition) => {
        const count = countOf(definition, widgets);
        const maxInstances = maxInstancesOf(definition);
        return { definition, count, multiple: maxInstances > 1, atLimit: count >= maxInstances };
      });
  }

  private isPermitted(definition: DashboardWidgetDefinition): boolean {
    return this.access.hasPermissions(definition.permissions ?? []);
  }
}

/** The registry of the dashboard being rendered, for the current user; for the host's providers */
export function dashboardWidgetRegistryFactory(): DashboardWidgetRegistry {
  const config = inject(DASHBOARD_CONFIG);
  return new DashboardWidgetRegistry(config.widgets, inject(DASHBOARD_ACCESS));
}

function countOf(definition: DashboardWidgetDefinition, widgets: readonly DashboardWidgetInstance[]): number {
  return widgets.filter((widget) => widget.type === definition.type).length;
}
